import { useState } from 'react';
import {
  GOAL_TYPE_LABEL,
  formatMoney,
  goalTypeSchema,
  toMinorUnits,
  type Goal,
  type GoalTypeValue,
} from '@teentack/shared';
import { useParentOverview } from '@/features/parent/use-parent-overview';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, SelectInput } from '@/components/ui/Input';
import { ErrorNote } from '@/components/Form';
import { presentApiError } from '@/lib/form-errors';
import { useCreateGoal, useDeleteGoal, useGoals, useUpdateGoal } from './use-goals';

/**
 * Goals, on both consoles. [Parent step 24]
 *
 * The guardian can set and clear them; the teen sees the same rows read-only.
 * Progress is whatever the server measured from real work, so this component
 * never computes it — it only draws it.
 */

/** Earnings are stored in minor units; everything else is a plain count. */
function describe(goal: Goal): string {
  if (goal.goalType === 'EARNINGS') {
    const current = formatMoney(Number(goal.currentValue ?? 0), 'USD');
    const target = formatMoney(Number(goal.targetValue), 'USD');
    return `${current} of ${target}`;
  }
  if (goal.goalType === 'EXPERIENCE_HOURS') {
    return `${goal.currentValue ?? 0} of ${goal.targetValue} jobs`;
  }
  return goal.targetValue;
}

function GoalRow({ goal, canEdit }: { goal: Goal; canEdit: boolean }) {
  const update = useUpdateGoal();
  const remove = useDeleteGoal();

  const pct = goal.progress === null ? null : Math.round(goal.progress * 100);
  const done = goal.status === 'ACHIEVED' || goal.progress === 1;

  return (
    <li className="rounded-2xl border border-line bg-white p-5 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-ink">{GOAL_TYPE_LABEL[goal.goalType]}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {goal.teenName} · {describe(goal)}
          </p>
        </div>
        <Badge tone={done ? 'safe' : goal.status === 'MISSED' ? 'neutral' : 'brand'}>
          {done ? 'Achieved' : goal.status.replace(/_/g, ' ').toLowerCase()}
        </Badge>
      </div>

      {pct !== null && (
        <div className="mt-3">
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className={`h-full rounded-full transition-all ${done ? 'bg-safe-600' : 'bg-brand-600'}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p
            className="mt-1 text-xs font-semibold text-slate-500"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {pct}%
          </p>
        </div>
      )}

      {goal.targetDate && (
        <p className="mt-2 text-xs text-slate-400">By {goal.targetDate}</p>
      )}

      {canEdit && (
        <div className="mt-3 flex gap-2 border-t border-line pt-3">
          {goal.status === 'IN_PROGRESS' && (
            <Button
              variant="ghost"
              disabled={update.isPending}
              onClick={() => update.mutate({ goalId: goal.id, status: 'ACHIEVED' })}
            >
              Mark achieved
            </Button>
          )}
          <Button
            variant="ghost"
            disabled={remove.isPending}
            onClick={() => remove.mutate(goal.id)}
          >
            Remove
          </Button>
        </div>
      )}
    </li>
  );
}

export function GoalsPanel({ canEdit }: { canEdit: boolean }) {
  const [adding, setAdding] = useState(false);
  const [teenId, setTeenId] = useState('');
  const [goalType, setGoalType] = useState<GoalTypeValue>('EARNINGS');
  const [target, setTarget] = useState('');
  const [error, setError] = useState<string | null>(null);

  const goals = useGoals();
  const overview = useParentOverview(canEdit);
  const create = useCreateGoal();

  const teens = (overview.data?.teens ?? []).filter((teen) => teen.verified);
  const items = goals.data ?? [];

  async function submit(): Promise<void> {
    setError(null);
    const chosen = teenId || teens[0]?.id;
    if (!chosen) {
      setError('Approve a teen account first.');
      return;
    }

    try {
      await create.mutateAsync({
        teenId: chosen,
        goalType,
        // Earnings are typed as money and stored in minor units, so £50 is
        // 5000 rather than 50 sitting next to a currency-formatted total.
        targetValue:
          goalType === 'EARNINGS' ? String(toMinorUnits(target.trim())) : target.trim(),
      });
      setTarget('');
      setAdding(false);
    } catch (caught) {
      setError(presentApiError(caught, 'Could not save that goal').message);
    }
  }

  // Nothing to say to a teen who has no goals set for them.
  if (!canEdit && items.length === 0) return null;

  return (
    <section>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-ink">
          {canEdit ? 'Goals you have set' : 'What you are working towards'}
        </h2>
        {canEdit && !adding && teens.length > 0 && (
          <Button variant="outline" onClick={() => setAdding(true)}>
            Set a goal
          </Button>
        )}
      </div>

      {adding && (
        <div className="mb-4 rounded-2xl border border-line bg-white p-5 shadow-soft">
          <div className="grid gap-3 sm:grid-cols-2">
            {teens.length > 1 && (
              <SelectInput
                label="For"
                value={teenId || teens[0]?.id || ''}
                onChange={(event) => setTeenId(event.target.value)}
              >
                {teens.map((teen) => (
                  <option key={teen.id} value={teen.id}>
                    {teen.name}
                  </option>
                ))}
              </SelectInput>
            )}

            <SelectInput
              label="Goal"
              value={goalType}
              onChange={(event) => setGoalType(event.target.value as GoalTypeValue)}
            >
              {goalTypeSchema.options.map((value) => (
                <option key={value} value={value}>
                  {GOAL_TYPE_LABEL[value]}
                </option>
              ))}
            </SelectInput>

            <Input
              label={
                goalType === 'EARNINGS'
                  ? 'Amount to earn'
                  : goalType === 'EXPERIENCE_HOURS'
                    ? 'Number of jobs'
                    : 'Skill to build'
              }
              value={target}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTarget(event.target.value)}
              placeholder={goalType === 'EARNINGS' ? '200.00' : goalType === 'EXPERIENCE_HOURS' ? '5' : 'Talking to customers'}
            />
          </div>

          {error && (
            <div className="mt-3">
              <ErrorNote message={error} />
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <Button disabled={create.isPending || target.trim().length === 0} onClick={() => void submit()}>
              {create.isPending ? 'Saving…' : 'Set goal'}
            </Button>
            <Button variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-line bg-white p-8 text-center text-sm text-slate-500">
          No goals yet. They turn a run of odd jobs into something with a shape.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((goal) => (
            <GoalRow key={goal.id} goal={goal} canEdit={canEdit} />
          ))}
        </ul>
      )}
    </section>
  );
}
